// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { Dictionary } from 'typescript-collections';
import { NotificationVO } from '@shared/vo/notification.vo';
import { SystemConfigurationService } from '@shared/system-configuration/system-configuration-loading.service';
import { CookieStorage } from '@shared/cookie-storage';
import { PropertyUtils } from '@article/util/property-utils';

export type SystemNotificationLocation = "main" | "login" | "news";

@Injectable({providedIn: 'root'})
export class SystemNotificationManager {
  private locationToNotifications: Dictionary<string, NotificationVO[]> = null;

  constructor(private localeResourceManager: LocaleResourceManager,
              private systemConfigurationService: SystemConfigurationService,
              private cookieStorage: CookieStorage) {
  }

  public async getNotificationsForLocation(location: SystemNotificationLocation): Promise<NotificationVO[]> {
    if (this.locationToNotifications != null) {
      return this.locationToNotifications.getValue(location);
    } else {
      const notifications = await this.systemConfigurationService.loadSystemNotifications();
      return this.filterAndStoreNewNotifications(notifications, location);
    }
  }

  public applyConfirmationToAllNotificationsAtLocation(location: SystemNotificationLocation): void {
    if (this.locationToNotifications === null) {
      return;
    }
    const notifications: NotificationVO[] = this.locationToNotifications.getValue(location);
    const temporaryNotifications = !!notifications ? notifications.filter(notification => !!notification.endDate) : [];
    if (temporaryNotifications.length === 0) {
      return;
    }
    let cookieExpireDate: Date = new Date();
    for (const temporaryNotification of temporaryNotifications) {
      if (cookieExpireDate.getTime() < temporaryNotification.endDate.getTime()) {
        cookieExpireDate = temporaryNotification.endDate;
      }
    }
    this.cookieStorage.setLastSystemNotificationConfirmationTimestampForLocation(location, new Date().getTime(), cookieExpireDate);
    const permanentNotifications: NotificationVO[] = notifications.filter(notification => !notification.endDate);
    this.locationToNotifications.setValue(location, permanentNotifications);
  }

  public includesTemporaryNotification(notifications: NotificationVO[]): boolean {
    const temporaryNotifications = !!notifications ? notifications.filter(notification => !!notification.endDate) : [];
    return temporaryNotifications.length > 0;
  }

  public getNotificationText(notification: NotificationVO): string {
    if (!notification) {
      return '';
    }
    return PropertyUtils.getLocalizedPropertyValueAsStringByLocaleChain(notification.value, this.localeResourceManager.getLocaleChain());
  }

  private filterAndStoreNewNotifications(notifications: NotificationVO[], requestedLocation: string): NotificationVO[] {
    this.locationToNotifications = new Dictionary<string, NotificationVO[]>();
    for (const notification of notifications) {
      let notificationLocation: string = notification.location;
      const timestamp: number = this.cookieStorage.getLastSystemNotificationConfirmationTimestampForLocation(notificationLocation);
      if (!timestamp || !notification.endDate || timestamp < notification.startDate.getTime()) {
        let storedNotifications: NotificationVO[] = this.locationToNotifications.getValue(notificationLocation);
        if (!storedNotifications) {
          storedNotifications = [];
          this.locationToNotifications.setValue(notificationLocation, storedNotifications);
        }
        storedNotifications.push(notification);
      }
    }
    return this.locationToNotifications.getValue(requestedLocation);
  }
}
